/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  notification,
  Row,
  Select,
  Table,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { IOrder, IOrderDDLRes, IOrderItem } from '@/interfaces/order';
import locale from 'antd/es/date-picker/locale/th_TH';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { round } from 'lodash';
import { DetailItem } from '@/components/dataItem/data-view-item';
import OrderSelector from '@/components/selector/order-selector';
import { getFullAddress } from '@/utils/customerUtils';
import Title from '@/components/title';
import OrderItemTable from '@/containers/order/maintain/components/order-info/components/order-item-table';
import { ICndn, ICndnItem } from '@/interfaces/cndn';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getOrder } from '@/adapters/order';
import { addCndn } from '@/adapters/cndn';
import dayjs from 'dayjs';
import { getQueryParam } from '@/utils/pathUtils';
import { getCndnTypeName, getCndnTypeOptions } from '@/helpers/cndnHelper';
import { ECndnReason, ECndnType } from '@/enums/ECndn';

const CreateCndn = () => {
  const _orderId = getQueryParam('orderId');
  const navigate = useNavigate();
  const [mainForm] = Form.useForm<Partial<ICndn>>();
  const [form] = Form.useForm();
  const [discount, setDiscount] = useState(0);
  const [vatRate, setVatRate] = useState(7);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<string>('');
  const [dataItem, setDataItem] = useState<Partial<ICndnItem>[]>([]);
  const isEditing = (record: Partial<IOrderItem>) => record.key === editingKey;
  const [orderId, setOrderId] = useState<number>(Number(_orderId || 0));
  const _type = Form.useWatch('type', mainForm);

  const { data: order } = useQuery<IOrder, Error>({
    queryKey: ['get-order', orderId],
    queryFn: async () => await getOrder(Number(orderId)),
    enabled: !!orderId,
  });

  const edit = (record: Partial<ICndnItem>) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key || '');
  };

  const save = async (key: string) => {
    try {
      const row = (await form.validateFields()) as Partial<ICndnItem>;
      const newData = [...dataItem];
      const index = newData.findIndex(item => key === item.key || '');
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          unitPrice: round(Number(item.unitPrice || 0), 2),
          quantity: round(Number(item.quantity || 0), 2),
          ...row,
        });
        setDataItem(newData);
        setEditingKey('');
        setEditingIndex('');
      } else {
        newData.push(row);
        setDataItem(newData);
        setEditingIndex('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  interface EditableCellProps extends React.HTMLAttributes<any> {
    editing: boolean;
    dataIndex: string;
    title: string;
    inputType: 'number' | 'text';
    record: Partial<ICndnItem>;
    index: number;
  }

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    record,
    editing,
    dataIndex,
    title,
    inputType,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === 'number' ? (
        <InputNumber
          className="w-full"
          type="number"
          onBlur={() => save(record.key || '')}
          onPressEnter={() => save(record.key || '')}
          autoFocus
          controls={false}
          decimalSeparator={'.'}
          precision={2}
          style={{ textAlignLast: 'right' }}
        />
      ) : (
        <Input onBlur={() => save(record.key || '')} />
      );

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity / rec.conversion, 2), 2),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 120,
      // editable: editingIndex === 'quantity',
      dataIndex: 'orderQuantity',
      className: 'font-bold',
      align: 'right',
      render: (_: number) => (
        <div className="font-bold w-full">{formatNumber(_, 2)}</div>
      ),
    },
    {
      title: 'จำนวนที่คืน',
      width: 120,
      editable: editingIndex === 'quantity',
      dataIndex: 'quantity',
      className: 'font-bold text-blue-700 bg-red-200',
      align: 'right',
      hidden: !_orderId,
      onHeaderCell: () => ({
        className: '!bg-red-700 !text-white', // Use the class here
      }),
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer"
          onClick={() => onRowClick(rec, 'quantity')}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: 'หน่วยขาย',
      width: 100,
      dataIndex: 'baseUnitName',
    },
    {
      title: 'ราคา',
      width: 120,
      dataIndex: 'orderUnitPrice',
      align: 'right',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: `ราคาที่${getCndnTypeName(Number(_type))}`,
      editable: editingIndex === 'unitPrice',
      width: 120,
      dataIndex: 'unitPrice',
      align: 'right',
      hidden: _orderId,
      onHeaderCell: () => ({
        className: '!bg-red-700 !text-white', // Use the class here
      }),
      className: 'font-bold text-blue-700 bg-red-200',
      render: (_: number, rec: ICndnItem) => (
        <div
          className="font-bold w-full cursor-pointer"
          onClick={() => onRowClick(rec, 'unitPrice')}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },

    {
      title: 'รวมเงิน',
      width: 150,
      dataIndex: 'totalPrice',
      align: 'right',
      className: 'font-bold',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity * (rec.unitPrice || 0), 2), 2),
    },
  ];

  const mergedColumns: any = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IOrderItem) => ({
        record,
        inputType: col.dataIndex === editingIndex ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const onRowClick = (record: Partial<IOrderItem>, index: string) => {
    edit(record);
    setEditingIndex(index);
  };

  const mutation = useMutation({
    mutationFn: async (data: Partial<ICndn>) => await addCndn(data),
    mutationKey: ['create-cndn'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'บันทึกใบลดหนี้/เพิ่มหนี้สำเร็จ',
        });
        navigate(`/cndn-note/${data?.data?.id}`);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    mainForm
      .validateFields()
      .then(async (values: Partial<ICndn>) => {
        const allUnitPricesZero = dataItem.every(item => item.unitPrice === 0);
        if (allUnitPricesZero) {
          message.warning('ต้องเพิ่มหนี้/ลดหนี้ อย่างน้อย 1 รายการ');
          return;
        }
        values.docDate = dayjs(values.docDate).toDate();
        values.taxRate = vatRate;
        values.vatAmount = vatPrice;
        values.discountAmount = discount;
        values.creditDebitNoteItems = dataItem;
        values.reason = _orderId
          ? ECndnReason.RETURN_GOOD
          : ECndnReason.CREDIT_PRICE;
        console.log('value', values);
        await mutation.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  useEffect(() => {
    if (order) {
      const initialOrderItems =
        order?.orderItems?.map(o => {
          const cndnItem: ICndnItem = {
            ...o,
            id: 0,
            key: uuidv4(),
            orderQuantity: Number(o.quantity),
            orderUnitPrice: Number(o.unitPrice),
            orderItemId: Number(o.id),
            unitPrice: _orderId ? Number(o.unitPrice) : 0,
            quantity: !_orderId ? Number(o.quantity) : 0,
          } as ICndnItem;
          return cndnItem;
        }) || [];

      setDataItem(initialOrderItems);
      setVatRate(order?.vatRate);
      setDiscount(Number(order?.discountAmount || 0));
    }
  }, [order]);

  const onOrderChange = (info: IOrderDDLRes) => {
    setOrderId(info.id);
    mainForm.setFieldsValue({
      orderId: info.id,
    });
  };

  let totalPrice = 0;
  let summaryPrice = 0;
  dataItem?.forEach(({ quantity, unitPrice }) => {
    totalPrice += Number(round(Number(quantity) * Number(unitPrice), 2));
  });
  const actualPrice =
    _type == ECndnType.CREDIT
      ? round(Number(order?.summary) - totalPrice, 2)
      : round(Number(order?.summary) + totalPrice, 2);
  const vatPrice = round(
    ((Math.abs(totalPrice) - discount) * vatRate) / 100,
    2,
  );
  summaryPrice = Math.abs(totalPrice) - discount + vatPrice;

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/cndn-note`)}
              >
                ใบเพิ่มหนี้/ลดหนี้
              </div>
            ),
          },
          {
            title: 'สร้างใหม่',
          },
        ]}
      />
      <div className="flex w-full mt-5">
        <Form
          form={mainForm}
          layout="vertical"
          className="w-full"
          autoComplete="off"
          initialValues={{
            orderId: _orderId && Number(_orderId || 0),
            type: _orderId && ECndnType.CREDIT,
          }}
        >
          <Row gutter={24}>
            <Col md={24} lg={16} xxl={18}>
              <Row gutter={12}>
                <Col
                  xs={24}
                  md={10}
                  style={{ display: _orderId ? 'none' : '' }}
                >
                  <Form.Item<ICndn>
                    label="เลขที่ออเดอร์"
                    name="orderId"
                    rules={[{ required: true, message: 'เลือกออเดอร์' }]}
                  >
                    <OrderSelector handleSelectData={onOrderChange} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item<ICndn>
                    label="ประเภท"
                    name="type"
                    rules={[{ required: true, message: 'เลือกประเภท' }]}
                  >
                    <Select
                      allowClear
                      className="md:min-w-[150px]"
                      size="large"
                      placeholder="เลือกประเภท"
                      options={getCndnTypeOptions()}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item<ICndn>
                    label="เลขที่ใบขอเพิ่มหนี้/ลดหนี้"
                    name="code"
                    rules={[
                      { required: true, message: 'เลขที่ใบขอเพิ่มหนี้/ลดหนี้' },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="เลขที่ใบขอเพิ่มหนี้/ลดหนี้"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={24}>
                  <Form.Item<ICndn> label="สาเหตุ" name="remark">
                    <Input size="large" placeholder="สาเหตุ" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col md={24} lg={8} xxl={6}>
              <Row gutter={12}>
                <Col xs={24} md={{ span: 12, offset: 12 }}>
                  <Form.Item<Partial<IOrder>>
                    label="วันที่"
                    name="orderCreateDate"
                    rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
                  >
                    <DatePicker
                      allowClear={false}
                      size="large"
                      className="w-full"
                      locale={locale}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
      {order && _type && (
        <>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`ออเดอร์`}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-3 border p-2 py-4 rounded-lg">
            <DetailItem label="เลขที่ออเดอร์ (OR)" detail={order?.code} />
            <DetailItem
              label="วันที่สั่งซื้อ"
              detail={formatDate(order?.orderCreateDate)}
            />
            <DetailItem
              label="วันที่ส่งสินค้า"
              detail={formatDate(order?.orderDate)}
            />
            <DetailItem
              label="วันที่ครบกำหนด"
              detail={`${formatDate(order?.dueDate)} (${order?.creditTerm || 0} วัน)`}
            />
            <DetailItem label="เลขที่ใบสั่งสินค้า (CO)" detail={order?.soNo} />
            <DetailItem label="เลขที่ใบแจ้งหนี้ (IV)" detail={order?.ivNo} />
            <DetailItem label="เลขที่ใบสั่งซื้อ (PO)" detail={order?.poNo} />
            <DetailItem
              label="รหัสลูกค้า"
              customClass="text-blue-700"
              detail={order.customerCode}
            />
            <DetailItem
              label="ลูกค้า"
              customClass="text-blue-700"
              detail={order.customerName}
            />
            <DetailItem label="BU" detail={order?.businessUnitName} />
            <DetailItem
              label="ที่อยู่ลูกค้า"
              detail={getFullAddress(order.customer)}
            />
            <DetailItem
              label="เบอร์โทรศัพท์"
              detail={order?.customer?.phoneNumber}
            />
            <DetailItem
              label="พนักงานขาย"
              customClass="text-blue-700"
              detail={order.salesName}
            />
            <DetailItem
              label="ประเภทออเดอร์"
              detail={order?.isSample ? 'ตัวอย่าง' : 'สินค้าขาย'}
              customClass="bg-yellow-500 p-0.5 rounded px-2 text-black"
            />
            <DetailItem label="หมายเหตุ" detail={order?.remark} />
            <DetailItem
              label="หมายเหตุเลื่อนส่ง"
              detail={order?.postponeRemark}
              customClass="bg-yellow-500 p-0.5 rounded px-2 text-black"
            />
          </div>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการตามใบแจ้งหนี้`}
          />
          <OrderItemTable
            order={order}
            orderItems={order.orderItems || []}
            showFooter={false}
          />
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการเพิ่มหนี้/ลดหนี้`}
          />
          <div className="flex w-full">
            <Form form={form} component={false}>
              <Table
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                locale={{
                  emptyText: 'เพิ่มรายการใหม่',
                }}
                size="small"
                bordered
                dataSource={dataItem}
                columns={mergedColumns}
                className="w-full"
                rowClassName="editable-row"
                pagination={false}
                scroll={{ x: 1200 }}
                summary={() => {
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          มูลค่าตามใบแจ้งหนี้เดิม
                        </Table.Summary.Cell>

                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(Number(order?.summary), 2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          มูลค่าที่ถูกต้อง
                        </Table.Summary.Cell>

                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(Number(actualPrice), 2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ผลต่าง
                        </Table.Summary.Cell>

                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(totalPrice, 2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ส่วนลด (บาท)
                        </Table.Summary.Cell>
                        <Table.Summary.Cell
                          index={5}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          <InputNumber
                            className="w-full"
                            value={discount}
                            style={{ textAlignLast: 'right' }}
                            controls={false}
                            decimalSeparator="."
                            formatter={value =>
                              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            parser={value =>
                              value?.replace(
                                /\$\s?|(,*)/g,
                                '',
                              ) as unknown as number
                            }
                            precision={2}
                            onChange={value => {
                              setDiscount(value || 0);
                            }}
                            // max={totalPrice}
                            min={0}
                          />
                        </Table.Summary.Cell>{' '}
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ยอดรวมหลังหักส่วนลด
                        </Table.Summary.Cell>
                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(Math.abs(totalPrice) - discount, 2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ภาษีมูลค่าเพิ่ม{' '}
                          <InputNumber
                            value={vatRate}
                            onChange={value => {
                              setVatRate(value || 0);
                            }}
                            disabled
                            min={0}
                            suffix="%"
                          />
                        </Table.Summary.Cell>
                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(vatPrice, 2)}
                        </Table.Summary.Cell>{' '}
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ยอดสุทธิ
                        </Table.Summary.Cell>
                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(summaryPrice, 2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Form>
          </div>
          <div className="flex justify-end mt-5 gap-2">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className=" font-bold min-w-[150px]"
              loading={mutation.isPending}
              onClick={() => handleUpdate()}
              // disabled={!actualPrice}
            >
              บันทึก
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default CreateCndn;
